/**
 * InterestPaymentsStep Component
 * Step 2: Interest rate, day count basis, and payment frequency settings
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Paper,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useContractFormContext } from '../ContractFormContext';

function InterestPaymentsStep() {
  const { control, watch, setValue } = useFormContext();
  const {
    refData,
    selectedBenchmarkRate,
    setSelectedBenchmarkRate,
    hasSchedule,
    setHasSchedule,
    setSchedulePreview,
  } = useContractFormContext();

  // T077: confirm before swapping the calculation profile when a schedule
  // already exists. The SP-side guard clears the schedule on save, but the
  // user deserves a heads-up first so they're not surprised when the
  // schedule preview empties on the next regenerate.
  const [pendingProfileChange, setPendingProfileChange] = React.useState(null);

  const rateType = watch('rate_type');
  const rateSpread = watch('rate_spread') || 0;
  const watchedBenchmarkRateId = watch('benchmark_rate_id');
  const selectedCountryId = watch('investment_currency_id');
  const selectedProfileId = watch('calculation_profile_id');
  const eligibleForRepricing = watch('eligible_for_repricing');
  const investmentTerm = watch('investment_term');
  const investmentTermValue = watch('investment_term_value');
  const depositDate = watch('deposit_date');
  const maturityDate = watch('maturity_date');
  const interestFreqId = watch('interest_paid_frequency_id');
  const capitalFreqId = watch('capital_paid_frequency_id');
  const combinePaymentRows = watch('combine_payment_rows');
  const equalInstallment = watch('equal_installment');

  // Compute contract term in months for re-pricing frequency validation
  const contractTermMonths = (() => {
    const value = parseInt(investmentTermValue, 10);
    if (!value || Number.isNaN(value)) return null;
    if (investmentTerm === 'Years') return value * 12;
    if (investmentTerm === 'Months') return value;
    if (investmentTerm === 'Days') return value / 30.44;
    return null;
  })();

  // Filter out Adhoc (no fixed cadence) and any frequency longer than the contract term
  const repricingFrequencyOptions = (refData.frequencies || []).filter((f) => {
    if (!f || typeof f.frequency !== 'string') return false;
    if (f.frequency.toLowerCase() === 'adhoc') return false;
    if (contractTermMonths != null && f.months_value && f.months_value > contractTermMonths) return false;
    return true;
  });

  // Get the currently selected profile for display
  const selectedProfile = selectedProfileId
    ? (refData.calculationProfiles || []).find(p => p.id === parseInt(selectedProfileId))
    : null;

  // Handle profile selection — auto-fill interest calc type and day count basis
  const handleProfileChange = (profileId) => {
    setValue('calculation_profile_id', profileId || null);
    if (profileId) {
      const profile = (refData.calculationProfiles || []).find(p => p.id === parseInt(profileId));
      if (profile) {
        // Auto-fill day count basis
        setValue('day_count_basis', String(profile.day_count_basis));
        // Auto-set capitalization frequency for SPC profiles
        if (profile.interest_method === 'Simple Periodic Capitalization') {
          const annualFreq = (refData.frequencies || []).find(f => f.frequency === 'Annually' || f.frequency === 'Annual');
          if (annualFreq) {
            setValue('capitalization_frequency_id', annualFreq.id);
          }
        } else {
          setValue('capitalization_frequency_id', '');
        }
        // T076: Annuity profiles require equal_installment + combine_payment_rows.
        // Force both on; the toggles render as read-only captions for Annuity.
        // For Simple, leave equal_installment alone (user choice). For everything
        // else (Compound, Compound Linear, SPC) clear equal_installment because
        // it's only supported on Simple + Annuity at the engine level.
        if (profile.interest_method === 'Annuity') {
          setValue('equal_installment', true);
          setValue('combine_payment_rows', true);
        } else if (profile.interest_method !== 'Simple') {
          setValue('equal_installment', false);
        }
      }
    }
  };

  // Filter to only active rates for the selected country and group by type
  const activeRates = refData.interestRates.filter(rate =>
    rate.active !== false &&
    (!selectedCountryId || rate.country_id === parseInt(selectedCountryId))
  );

  const groupedRates = activeRates.reduce((acc, rate) => {
    const type = rate.interest_rate_type || rate.rate_type || 'Other';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(rate);
    return acc;
  }, {});

  // Get the latest rate for each type
  const latestRatesByType = Object.entries(groupedRates).map(([type, rates]) => {
    const sorted = [...rates].sort((a, b) => new Date(b.effective_date) - new Date(a.effective_date));
    return sorted[0];
  }).filter(Boolean);

  // Ensure the saved benchmark rate is in the options (it may be an older rate, not the latest)
  if (watchedBenchmarkRateId) {
    const savedRate = refData.interestRates.find(r => r.id === parseInt(watchedBenchmarkRateId));
    if (savedRate && !latestRatesByType.find(r => r.id === savedRate.id)) {
      latestRatesByType.push(savedRate);
    }
  }

  // Get benchmark rate details for Variable contracts
  const selectedBenchmark = watchedBenchmarkRateId
    ? refData.interestRates.find(r => r.id === parseInt(watchedBenchmarkRateId))
    : null;
  const benchmarkRateValue = selectedBenchmark
    ? parseFloat(selectedBenchmark.effective_interest_rate || selectedBenchmark.rate || 0)
    : 0;
  const effectiveRate = benchmarkRateValue + parseFloat(rateSpread || 0);

  // Handle benchmark rate selection (Variable contracts only)
  const handleBenchmarkRateChange = (event) => {
    const selectedId = event.target.value;
    setSelectedBenchmarkRate(selectedId);
    if (selectedId) {
      const rate = refData.interestRates.find(r => r.id === parseInt(selectedId));
      if (rate) {
        const rateValue = rate.effective_interest_rate || rate.rate;
        setValue('benchmark_rate_id', rate.id);
        // Calculate effective rate with spread
        const spread = parseFloat(rateSpread) || 0;
        setValue('interest_rate', parseFloat(rateValue) + spread);
      }
    } else {
      setValue('benchmark_rate_id', null);
    }
  };

  // Handle spread change for Variable contracts
  const handleSpreadChange = (newSpread) => {
    setValue('rate_spread', newSpread);
    if (rateType === 'Variable' && benchmarkRateValue > 0) {
      setValue('interest_rate', benchmarkRateValue + parseFloat(newSpread || 0));
    }
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Interest Settings
          </Typography>

          <Grid container spacing={3}>
            {/* Rate Type */}
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="rate_type"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Rate Type *"
                    fullWidth
                    onChange={(e) => {
                      field.onChange(e);
                      // Clear Variable-specific fields when switching away from Variable
                      if (e.target.value !== 'Variable') {
                        setValue('rate_spread', 0);
                        setValue('benchmark_rate_id', null);
                        setValue('rate_change_effective_basis', 'immediate');
                        setSelectedBenchmarkRate('');
                      }
                      // Clear repricing fields when switching away from Floating
                      if (e.target.value !== 'Floating') {
                        setValue('eligible_for_repricing', false);
                        setValue('repricing_frequency_id', '');
                        setValue('first_repricing_date', '');
                      }
                    }}
                  >
                    <MenuItem value="Fixed">Fixed</MenuItem>
                    <MenuItem value="Variable">Variable</MenuItem>
                    <MenuItem value="Floating">Floating</MenuItem>
                  </TextField>
                )}
              />
            </Grid>

            {/* Benchmark Rate - Variable contracts only */}
            {rateType === 'Variable' && latestRatesByType.length > 0 && (
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="benchmark_rate_id"
                  control={control}
                  rules={{ required: rateType === 'Variable' ? 'Benchmark rate is required for Variable contracts' : false }}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl fullWidth error={!!error}>
                      <InputLabel id="benchmark-rate-label">
                        Benchmark Rate {rateType === 'Variable' ? '*' : ''}
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="benchmark-rate-label"
                        label={`Benchmark Rate ${rateType === 'Variable' ? '*' : ''}`}
                        value={field.value || ''}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          handleBenchmarkRateChange(e);
                        }}
                      >
                        <MenuItem value="">
                          <em>Select benchmark rate...</em>
                        </MenuItem>
                        {latestRatesByType.map((rate) => {
                          const benchmarkType = rate.interest_rate_type || rate.rate_type;
                          const benchmarkValue = rate.effective_interest_rate || rate.rate;
                          return (
                            <MenuItem key={rate.id} value={rate.id}>
                              {benchmarkType}: {benchmarkValue}%
                            </MenuItem>
                          );
                        })}
                      </Select>
                      {error && <Typography variant="caption" color="error">{error.message}</Typography>}
                    </FormControl>
                  )}
                />
                <Typography variant="caption" color="text.secondary">
                  {rateType === 'Variable'
                    ? 'The benchmark rate this contract follows (e.g., Prime, JIBAR)'
                    : 'Select to auto-fill interest rate (optional for tracking)'}
                </Typography>
              </Grid>
            )}

            {/* Rate Spread - for Variable contracts */}
            {rateType === 'Variable' && (
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="rate_spread"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Rate Spread (%)"
                      type="number"
                      fullWidth
                      inputProps={{ step: 0.01 }}
                      value={field.value || 0}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        handleSpreadChange(e.target.value);
                      }}
                      helperText="+ or - adjustment from benchmark (e.g., Prime + 2% = spread of 2)"
                    />
                  )}
                />
              </Grid>
            )}

            {/* Rate Change Effective Basis - Variable contracts only */}
            {rateType === 'Variable' && (
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="rate_change_effective_basis"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Rate Change Effective Basis"
                      fullWidth
                      value={field.value || 'immediate'}
                      helperText="When benchmark rate changes take effect on this contract"
                    >
                      <MenuItem value="immediate">On Date of Change</MenuItem>
                      <MenuItem value="anniversary">On Annual Anniversary</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
            )}

            {/* Rate Change Mode - Variable contracts only.
                Default 'bulk' = contract is included in benchmark sweeps.
                'manual' = excluded from sweeps, rate is only changed via per-contract adhoc requests. */}
            {rateType === 'Variable' && (
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="rate_change_mode"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Rate Update Mode"
                      fullWidth
                      value={field.value || 'bulk'}
                      helperText={
                        (field.value || 'bulk') === 'manual'
                          ? 'Excluded from benchmark sweeps. Rate only changes via per-contract adhoc requests.'
                          : 'Included in benchmark rate sweeps.'
                      }
                    >
                      <MenuItem value="bulk">Bulk benchmark sweep</MenuItem>
                      <MenuItem value="manual">Manual rate updates only</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
            )}

            {/* Effective Rate Display - for Variable contracts */}
            {rateType === 'Variable' && selectedBenchmark && (
              <Grid size={12}>
                <Alert severity="info" icon={false}>
                  <Typography variant="body2">
                    <strong>Effective Rate:</strong>{' '}
                    {selectedBenchmark.interest_rate_type || selectedBenchmark.rate_type} ({benchmarkRateValue.toFixed(2)}%)
                    {parseFloat(rateSpread) !== 0 && (
                      <> {parseFloat(rateSpread) >= 0 ? '+' : ''} {parseFloat(rateSpread).toFixed(2)}%</>
                    )}
                    {' = '}
                    <Chip
                      label={`${effectiveRate.toFixed(2)}%`}
                      color="primary"
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Typography>
                </Alert>
              </Grid>
            )}

            {/* Calculation Profile */}
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="calculation_profile_id"
                control={control}
                rules={{ required: 'Calculation profile is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    select
                    label="Calculation Profile *"
                    fullWidth
                    value={field.value || ''}
                    error={!!error}
                    helperText={error?.message || 'Determines interest method, day count, and compounding'}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      // If a schedule already exists and the user is switching
                      // to a *different* profile, surface the consequence
                      // before applying. The SP will clear the schedule on the
                      // next save; the dialog gives the user a chance to back
                      // out before they commit.
                      if (hasSchedule
                          && newValue
                          && field.value
                          && String(newValue) !== String(field.value)) {
                        setPendingProfileChange({
                          fromId: field.value,
                          toId: newValue,
                          apply: () => {
                            field.onChange(newValue);
                            handleProfileChange(newValue);
                          },
                        });
                        return;
                      }
                      field.onChange(newValue);
                      handleProfileChange(newValue);
                    }}
                  >
                    <MenuItem value="">
                      <em>Select calculation profile...</em>
                    </MenuItem>
                    {(refData.calculationProfiles || []).filter(p => p.active !== false).map((profile) => (
                      <MenuItem key={profile.id} value={profile.id}>
                        {profile.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            {/* Capitalization Frequency — only for SPC profiles */}
            {selectedProfile?.interest_method === 'Simple Periodic Capitalization' && (
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="capitalization_frequency_id"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      select
                      label="Capitalization Frequency"
                      fullWidth
                      value={field.value || ''}
                      error={!!error}
                      helperText={error?.message || 'How often accrued interest is added to principal'}
                    >
                      <MenuItem value="">
                        <em>Select frequency...</em>
                      </MenuItem>
                      {(refData.frequencies || []).filter(f => f.active !== false).map((freq) => (
                        <MenuItem key={freq.id} value={freq.id}>
                          {freq.frequency}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
            )}

            {/* Interest Rate */}
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="interest_rate"
                control={control}
                rules={{
                  required: 'Interest rate is required',
                  validate: (value) => {
                    const num = parseFloat(value);
                    if (isNaN(num)) return 'Must be a valid number';
                    if (num < 0) return 'Cannot be negative';
                    return true;
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Interest Rate (%) *"
                    type="number"
                    fullWidth
                    inputProps={{ step: 0.01, min: 0 }}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Profile formula display */}
          {selectedProfile && (
            <Paper variant="outlined" sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5' }}>
              <Typography variant="subtitle2" gutterBottom>
                {selectedProfile.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {selectedProfile.description}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', backgroundColor: '#fff', p: 1, borderRadius: 1 }}>
                {selectedProfile.formula_display}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={selectedProfile.interest_method} size="small" variant="outlined" />
                <Chip label={selectedProfile.day_count_convention} size="small" variant="outlined" />
                {selectedProfile.compounding_frequency && selectedProfile.interest_method !== 'Simple' && (
                  <Chip label={`Compounding: ${selectedProfile.compounding_frequency}x/year`} size="small" variant="outlined" />
                )}
              </Box>
            </Paper>
          )}

          {/* Exclude Leap Day toggle */}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="exclude_leap_day"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value || false}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Exclude Leap Day"
                  />
                )}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                When enabled, February 29 is excluded from interest day counts (February always has 28 days)
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Payment Frequencies
          </Typography>

          {/* T064: Switch | Interest | Capital — three equal columns. Top-aligned
              so the switch tracks the field labels, not their centres. The switch
              column wraps in a flex so the control sits at the same baseline as
              the TextField inputs (which have label space above). */}
          <Grid container spacing={3} alignItems="flex-start">
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 56 }}>
                <Controller
                  name="combine_payment_rows"
                  control={control}
                  render={({ field }) => {
                    // T076: Annuity profiles require combine_payment_rows = true.
                    // Force on and lock the toggle so the user can see the state but can't change it.
                    const isAnnuity = selectedProfile?.interest_method === 'Annuity';
                    return (
                      <>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={field.value === true}
                              disabled={isAnnuity}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                field.onChange(checked);
                                if (checked && interestFreqId) {
                                  setValue('capital_paid_frequency_id', interestFreqId);
                                } else if (!checked) {
                                  setValue('equal_installment', false);
                                }
                              }}
                            />
                          }
                          label="Combined payment"
                        />
                        {isAnnuity && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            Required by Annuity profile.
                          </Typography>
                        )}
                      </>
                    );
                  }}
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name="interest_paid_frequency_id"
                control={control}
                rules={{ required: 'Interest payment frequency is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    select
                    label="Interest Payment Frequency *"
                    fullWidth
                    error={!!error}
                    helperText={error?.message || ' '}
                    onChange={(e) => {
                      field.onChange(e);
                      if (combinePaymentRows) {
                        setValue('capital_paid_frequency_id', e.target.value);
                      }
                    }}
                  >
                    <MenuItem value="">
                      <em>Select frequency...</em>
                    </MenuItem>
                    {refData.frequencies.map((freq) => (
                      <MenuItem key={freq.id} value={freq.id}>
                        {freq.frequency}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name="capital_paid_frequency_id"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    select
                    label="Capital Payment Frequency"
                    fullWidth
                    disabled={combinePaymentRows === true}
                    error={!!error}
                    helperText={
                      combinePaymentRows
                        ? 'Locked to interest payment frequency'
                        : (error?.message || 'Optional: Defaults to "At Maturity" if not specified')
                    }
                  >
                    <MenuItem value="">
                      <em>At Maturity (default)</em>
                    </MenuItem>
                    {refData.frequencies.map((freq) => (
                      <MenuItem key={freq.id} value={freq.id}>
                        {freq.frequency}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
          </Grid>

          {/* Equal-installment follow-up — depends on combined-payment being on.
              Three states:
                - Annuity profile: equal_installment is force-true and the toggle is
                  hidden (replaced with a read-only caption). The behaviour is
                  intrinsic to Annuity; the user has no choice to make.
                - Simple profile: the toggle is enabled and the user chooses.
                - Anything else (Compound, Compound Linear, SPC): toggle disabled +
                  alert explaining Simple/Annuity profiles only. */}
          {combinePaymentRows && (() => {
            const profileMethod = selectedProfile?.interest_method;
            const isAnnuity = profileMethod === 'Annuity';
            const isSimple = !profileMethod || profileMethod === 'Simple';
            if (isAnnuity) {
              return (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Equal-installment is on by default for Annuity profiles.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Engine solves for a constant total payment using the periodic rate r/N (N = compounding frequency), then accrues interest on actual calendar days. The payment is re-solved automatically at each rate change.
                  </Typography>
                </Box>
              );
            }
            return (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Controller
                  name="equal_installment"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value === true}
                          disabled={!isSimple}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Use equal-installment repayment"
                    />
                  )}
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                  Engine solves for a constant total payment that amortises the loan to zero by maturity. The payment is re-solved automatically at each rate change.
                </Typography>
                {!isSimple && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Equal-installment supports Simple-interest and Annuity profiles only. Select a Simple profile (e.g. Simple Actual/365) or the Equal Instalment Annuity (Monthly) profile to enable.
                  </Alert>
                )}
              </Box>
            );
          })()}

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            Note: Capital payment frequency cannot be more frequent than interest payment frequency.
          </Typography>
        </CardContent>
      </Card>

      {rateType === 'Floating' && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Re-pricing
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Set the cadence and effective date for re-pricing this Floating-rate contract. Upcoming re-pricing dates will appear on the Dashboard's Upcoming Events list.
            </Typography>

            <Grid container spacing={3} alignItems="center">
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="eligible_for_repricing"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!field.value}
                          onChange={(e) => {
                            field.onChange(e.target.checked);
                            if (!e.target.checked) {
                              setValue('repricing_frequency_id', '');
                              setValue('first_repricing_date', '');
                            }
                          }}
                        />
                      }
                      label="Eligible for re-pricing"
                    />
                  )}
                />
              </Grid>

              {eligibleForRepricing && (
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <Controller
                    name="repricing_frequency_id"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (!eligibleForRepricing) return true;
                        if (!value) return 'Re-pricing frequency is required';
                        const selected = (refData.frequencies || []).find(
                          (f) => f.id === parseInt(value, 10)
                        );
                        if (!selected) return true;
                        if (selected.frequency && selected.frequency.toLowerCase() === 'adhoc') {
                          return 'Adhoc cannot be used for re-pricing';
                        }
                        if (
                          contractTermMonths != null &&
                          selected.months_value &&
                          selected.months_value > contractTermMonths
                        ) {
                          return 'Re-pricing frequency cannot exceed the contract term';
                        }
                        return true;
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        select
                        label="Re-pricing Frequency *"
                        fullWidth
                        error={!!error}
                        helperText={error?.message || 'How often the rate should be reviewed'}
                      >
                        <MenuItem value="">
                          <em>Select frequency...</em>
                        </MenuItem>
                        {repricingFrequencyOptions.map((freq) => (
                          <MenuItem key={freq.id} value={freq.id}>
                            {freq.frequency}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
              )}

              {eligibleForRepricing && (
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <Controller
                    name="first_repricing_date"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (!eligibleForRepricing) return true;
                        if (!value) return 'First re-pricing date is required';
                        if (depositDate && value <= depositDate) {
                          return 'Must be after the deposit date';
                        }
                        if (maturityDate && value >= maturityDate) {
                          return 'Must be before the maturity date';
                        }
                        return true;
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="First Re-pricing Date *"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          min: depositDate || undefined,
                          max: maturityDate || undefined,
                        }}
                        error={!!error}
                        helperText={error?.message || 'When the first re-pricing event takes effect'}
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* T077: profile-change confirm dialog. Triggered when the user picks a
          different calculation profile while a schedule is already generated.
          On confirm, the new profile is applied and the schedule will be
          cleared on the next save (SP-side enforcement in sp_contract_update). */}
      <Dialog
        open={pendingProfileChange !== null}
        onClose={() => setPendingProfileChange(null)}
      >
        <DialogTitle>Change calculation profile?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This contract already has a generated schedule. Changing the
            calculation profile from <strong>{
              (refData.calculationProfiles || []).find(p => p.id === parseInt(pendingProfileChange?.fromId, 10))?.name || '—'
            }</strong> to <strong>{
              (refData.calculationProfiles || []).find(p => p.id === parseInt(pendingProfileChange?.toId, 10))?.name || '—'
            }</strong> will clear the current schedule. You will need to
            regenerate it before submitting the contract for approval.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingProfileChange(null)}>Cancel</Button>
          <Button
            onClick={() => {
              pendingProfileChange?.apply?.();
              // Optimistically clear the local schedule state. The SP will
              // delete the persisted rows on the next save (sp_contract_update
              // detects the calculation_profile_id change and clears).
              setHasSchedule(false);
              setSchedulePreview(null);
              setPendingProfileChange(null);
            }}
            color="primary"
            variant="contained"
          >
            Change profile
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default InterestPaymentsStep;
